import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * ROTAS69: the dropdown's results render through sections/r69-search-cards.liquid — a JSON-group
 * section whose product results are REAL collection cards. A JSON instance's fetchable id is the
 * full 'sections--N__key' string, which no hardcode can know — but the r69-card-seed section from
 * the same footer group is server-rendered on every page, and its auto wrapper carries that id.
 * Derive the sibling's id from it. Returns null when the seed is absent (the fetch then bails,
 * as it did when data-section-id was missing).
 *
 * The section wraps its payload in <template data-r69-search-payload> so the copy the REAL /search
 * page renders in its footer stays inert — unwrap it here after every fetch.
 * @returns {string | null}
 */
function r69SearchSectionId() {
  const seed = document.querySelector('[id^="shopify-section-sections--"][id$="__r69_card_seed"]');
  return seed ? seed.id.replace('shopify-section-', '').replace('__r69_card_seed', '__r69_search_cards') : null;
}

/**
 * ONE rule for what the search engine is asked, shared by the panel's cards, the "(N)" count,
 * Enter in the header and the results page's own input (twin: search-page-input.js carries the
 * same function — keep the two identical). The panel and the URL Enter opens send the same
 * string, so the count and the order cannot disagree.
 *
 * The query goes as typed, whitespace collapsed and WITHOUT `*`. The engine prefix-matches the
 * last word by itself on every locale, and any `*` makes it return the whole catalog: "bismark"
 * 11 products, "bismark*" 770; "0196" 1 (that SKU), "0196*" 770; even "zzqxv*" 770; bare "bism"
 * 14, "ausk" 258, "019" 3 — alike on lv, ru, lt, et and en (Storefront API, 02.10.2026). From
 * 06.09.2026 the theme appended a trailing `*`, because lv then prefix-matched only with it
 * ("ausk" 16, "ausk*" 236); the engine has changed since, and the star turned every search into
 * the full catalog. A star the shopper types is dropped for the same reason.
 * @param {string} raw - what the shopper typed
 * @returns {string} the query for the engine
 */
export function r69EngineQuery(raw) {
  return raw.replace(/\*/g, ' ').trim().replace(/\s+/g, ' ');
}

/**
 * @param {string} markup - raw section-render response
 * @returns {Element | null} the payload root (#predictive-search-results) or null
 */
function r69UnwrapPayload(markup) {
  const doc = new DOMParser().parseFromString(markup, 'text/html');
  const template = doc.querySelector('template[data-r69-search-payload]');
  return template ? template.content.firstElementChild : null;
}

/**
 * Moves the pages/articles part of the aside (its eyebrow + list) and the "no products" hint from
 * the pages-only render into the products-only render, so one panel shows both. The products
 * render never has pages (type=product), the pages render never has cards; each is right about
 * its own half. Policies and menu links are matched in Liquid from the query and are the same in
 * both, so the pages render's list — which has them too — simply replaces the products render's.
 * @param {Element} target - the products render (kept)
 * @param {Element | null} pages - the pages render (donor)
 */
function r69GraftPages(target, pages) {
  if (!pages) return;
  const aside = target.querySelector('.r69s-results__side');
  const eyebrow = pages.querySelector('.r69s-results__eyebrow--spaced');
  const list = pages.querySelector('.r69s-results__links--pages');
  if (aside && list) {
    aside.querySelector('.r69s-results__eyebrow--spaced')?.remove();
    aside.querySelector('.r69s-results__links--pages')?.remove();
    if (eyebrow) aside.append(eyebrow);
    aside.append(list);
  }
  // The hint ("nothing found — see the pages / choose a collection") is rendered only when there
  // are no cards; the pages render knows whether there ARE pages, the products render does not.
  const hint = target.querySelector('.r69s-results__hint');
  const donorHint = pages.querySelector('.r69s-results__hint');
  if (hint && donorHint) hint.replaceWith(donorHint);
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    // NO preload on page load. This used to morph the whole panel at idle just to have the
    // recently-viewed row ready, which now costs a Section Rendering response of full product
    // cards (measured: 209KB for four) parsed and mounted on the main thread of every page —
    // a visible hitch on a phone, for a panel most visitors never open. The row is filled when
    // the panel actually opens (search-modal.liquid's healer), which also keeps it correct
    // after any later wipe.
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });

    // Focus belongs to keyboard navigation only. `#resetSearch` sets -1, and stealing focus there
    // means focusing the header field on a page nobody touched: the browser scrolls that field
    // into view (the page jumps to the top) and the field's own focus handler opens the panel,
    // which locks the page scroll on a phone — the page then refuses to scroll at all. The early
    // return above used to hide this, because a closed panel held no result items; it stops
    // hiding it the moment the recently-viewed row is filled in the background.
    if (index >= 0) this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          event.preventDefault();
          this.#goToResultsPage();
        }
        break;
      }
    }
  };

  /**
   * The form's own submit (the phone keyboard's search key, a submit button): same destination as
   * Enter, so the results page always gets the engine query and the products-only type.
   * @param {Event} event
   */
  onSubmit(event) {
    event.preventDefault();
    this.#goToResultsPage();
  }

  /**
   * Opens the results page for what is typed: the engine form of the query (r69EngineQuery) and
   * type=product — exactly the parameters the panel's count was fetched with, so the page's
   * total, order and cards match the panel. The old handler sent the raw text without a type:
   * the page then paginated products + articles + pages and its total was 2 higher than the
   * panel's "(N)" on every query (06.09.2026).
   */
  #goToResultsPage() {
    const searchUrl = new URL(Theme.routes.search_url, location.origin);
    searchUrl.searchParams.set('q', r69EngineQuery(this.refs.searchInput.value));
    searchUrl.searchParams.set('type', 'product');
    window.location.href = searchUrl.toString();
  }

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    // #loadEmptyState calls this with keepFocus=false on the first dialog open, to morph
    // the recently-viewed row in. That call is debounced, so the shopper can already be
    // typing when it lands — wiping the input would eat their first characters and morph
    // the panel away, so skip the reset in that case.
    if (keepFocus === false && this.refs.searchInput.value.trim() !== '') return;
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    const sectionId = r69SearchSectionId();
    if (!sectionId) return;

    // ONE engine for every query length: the full search (title + description + tags + variant
    // SKUs). The old 1–3-char predictive typeahead is gone with the switch to real collection
    // cards: the predictive endpoint cannot render a JSON-instance section. Prefix matching is the
    // engine's own (see r69EngineQuery) — `options[prefix]=last` does nothing on this store.
    //
    // TWO fetches of the same section. The cards and the "(N)" come from a PRODUCTS-ONLY search:
    // that is the exact request the results page runs (type=product), and the engine ranks
    // products differently when pages and articles are in the same search — with one mixed
    // fetch the panel's 5th and 12th card were not the page's 5th and 12th ("ausk*",
    // 06.09.2026), and the count had to be a third request. Pages and articles for the aside come
    // from a second, cards-free fetch and are grafted in below.
    const engineQuery = r69EngineQuery(searchTerm);
    // nothing but stars typed — nothing to ask the engine: the panel behaves as for an empty input
    if (engineQuery === '') {
      this.#resetSearch();
      return;
    }
    const buildUrl = (type) => {
      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set('q', engineQuery);
      url.searchParams.set('type', type);
      // view=r69-panel is the marker sections/r69-search-cards.liquid keys on (template.suffix): it
      // renders its payload only for section-rendering fetches and skips the 423 KB inert
      // <template> copy the real /search page otherwise carried in its footer (06.09.2026). The
      // alternate template templates/search.r69-panel.json exists only to carry the suffix.
      url.searchParams.set('view', 'r69-panel');
      return url;
    };

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();
    // An aborted fetch is routine while typing (each keystroke cancels the previous request), and
    // this promise is rejected for it below. Nothing listens for that rejection, so it surfaced in
    // the console as «Uncaught (in promise) Error: Fetch aborted by user» on every fast query
    // (owner, 06.09.2026). Listeners of the event still get the rejection through event.promise.
    deferredPromise.promise.catch(() => {});

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    Promise.all([
      sectionRenderer.getSectionHTML(sectionId, false, buildUrl('product')),
      // A failed pages fetch must not cost the products: the aside just keeps its collections.
      sectionRenderer.getSectionHTML(sectionId, false, buildUrl('article,page')).catch(() => null),
    ])
      .then(([resultsMarkup, pagesMarkup]) => {
        const payload = resultsMarkup ? r69UnwrapPayload(resultsMarkup) : null;

        if (!payload) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        r69GraftPages(payload, pagesMarkup ? r69UnwrapPayload(pagesMarkup) : null);
        morph(predictiveSearchResults, payload.outerHTML);
        this.#mirrorResultsState();

        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    const sectionId = r69SearchSectionId();
    if (!sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    // History is history: a product the shopper actually opened stays in this row after it
    // sells out. Search hides out-of-stock products by default (Search & Discovery), which
    // silently pruned sold-but-still-listed items from the row — the carousel and the wishlist
    // pass the same flag for the same reason. Draft/unlisted products are never returned
    // regardless, which is correct.
    url.searchParams.set('options[unavailable_products]', 'show');
    // Same marker as #onInput's fetch (view=r69-panel): without it the section renders no payload
    // and the recently-viewed row came back empty after a query was cleared (06.09.2026).
    url.searchParams.set('view', 'r69-panel');

    return sectionRenderer.getSectionHTML(sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      // The section wraps its payload in an inert <template> — unwrap before reading.
      const recentPayload = r69UnwrapPayload(recentlyViewedMarkup);
      const recentlyViewedProductsHtml = recentPayload && recentPayload.querySelector('#predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#mirrorResultsState();
    this.#resetScrollPositions();
  };

  /**
   * ROTAS69: "results are showing" as a class on the component, refreshed after every morph.
   * The stylesheets used to read `.predictive-search:has(.predictive-search-dropdown)` (Horizon ×4,
   * search-modal ×2), which made this 1 500-element component a :has() anchor: Blink re-walked it
   * on every mutation inside — each lazy image `src` swap, the reset button's `hidden`, the field's
   * :placeholder-shown — 14 re-evaluations on one Escape, and the same on every keystroke's morph
   * (collection page, 06.09.2026). One querySelector here, and the rules key on the class.
   */
  #mirrorResultsState() {
    this.classList.toggle('r69-has-results', !!this.refs.predictiveSearchResults.querySelector('.predictive-search-dropdown'));
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
